import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { createEnum } from "@/lib/utils/createEnum";
import { useTranslation } from "react-i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type C3Position = {
  id: number;
  date?: string;
  strategy_from_id?: number;
  strategy_from_name?: string;
  strategy_to_id?: number;
  strategy_to_name?: string;
  quantity?: number;
  weight?: number;
  market_value_local_ccy?: number;
  market_value_ptf_ccy?: number;
};

export const c3PositionFields = createEnum<C3Position>();

export const useC3PositionColumns = () => {
  const { t } = useTranslation([Tables.C3_POSITIONS, "tables"]);

  const columns: ColumnDef<C3Position>[] = [
    {
      accessorKey: c3PositionFields.id,
      header: "Id",
    },
    {
      accessorKey: c3PositionFields.date,
      header: () => t("columns.date", { ns: Tables.C3_POSITIONS }),
      cell: ({ row }) => (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue(c3PositionFields.date)))}
        </div>
      ),
    },
    {
      accessorKey: c3PositionFields.strategy_from_name,
      header: () =>
        t("columns.strategy_from_name", { ns: Tables.C3_POSITIONS }),
      cell: ({ row }) => (
        <Link
          href={toWorkspacePath(
            `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_from_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.original.strategy_from_name}
        </Link>
      ),
    },
    {
      accessorKey: c3PositionFields.strategy_to_name,
      header: () => t("columns.strategy_to_name", { ns: Tables.C3_POSITIONS }),
      cell: ({ row }) => (
        <Link
          href={toWorkspacePath(
            `/admin/${Clients.QTL_REFERENTIAL}/${Tables.STRATEGIES}/${row.original.strategy_to_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.original.strategy_to_name}
        </Link>
      ),
    },
    {
      accessorKey: c3PositionFields.quantity,
      header: () => (
        <div className="text-right">
          {t("columns.quantity", { ns: Tables.C3_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c3PositionFields.quantity))}
        </div>
      ),
    },
    {
      accessorKey: c3PositionFields.weight,
      header: () => (
        <div className="text-right">
          {t("columns.weight", { ns: Tables.C3_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c3PositionFields.weight))}
        </div>
      ),
    },
    {
      accessorKey: c3PositionFields.market_value_ptf_ccy,
      header: () => (
        <div className="text-right">
          {t("columns.market_value_ptf_ccy", { ns: Tables.C3_POSITIONS })}
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right">
          {formatNumber(row.getValue(c3PositionFields.market_value_ptf_ccy))}
        </div>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue(c3PositionFields.id)}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      ),
    },
  ];

  return columns;
};
